import React from 'react';

import { Chip, FormSection } from '../../ui';
import MiniaturaFotoVistoria from './MiniaturaFotoVistoria';
import { ChecklistAgrupado } from '../../utils/vistoriaAcessorios';

/**
 * O documento da vistoria — **um componente para as três telas que o exibem**
 * (at-64, frente D/D2).
 *
 * ## Por que existe
 *
 * O mesmo documento era renderizado **três vezes**, de fontes diferentes:
 *
 *   • `/vistoria/laudo` — do Redux (com blob URLs) + duas chamadas à API;
 *   • `/vistoria/veiculo/:id/laudo` — de `GET /vistorias/carro/:id/ultima`;
 *   • a prévia antes de assinar — do Redux + três chamadas redundantes.
 *
 * As três tinham a própria cópia das seções, e duas tinham cópia de
 * `groupChecklist` e de `normalizeImageUrl`. Convenção da at-24: regra repetida
 * em telas vira componente.
 *
 * 🔴 **Componente BURRO.** Não busca nada, não normaliza URL (isso é
 * `utils/vistoriaImagens.ts`), não agrupa o checklist (`utils/vistoriaAcessorios.ts`)
 * e **não ordena as fotos** — quem entrega o shape decide a ordem. Cada tela
 * resolve a sua fonte e passa o mesmo objeto.
 *
 * 🔴 Mora em `components/` e não em `src/ui` porque conhece o domínio: sabe o que
 * é uma vistoria, uma avaria, um vistoriador. Os componentes do kit que ele usa
 * (`FormSection`, `Chip`, e a `MiniaturaFotoVistoria` de domínio) são puros —
 * mesma relação do `FormularioAssociado` (at-58) com o kit.
 */

/** Uma foto já pronta para exibição: URL normalizada, ordem já definida. */
export interface FotoDoDocumento {
  url?: string;
  legenda?: string;
  avaria?: boolean;
  /** Botões da miniatura (ex.: "Visualizar" no laudo por veículo). */
  acoes?: React.ReactNode;
}

/** Quais seções o documento mostra. Ausente = mostra se houver dado. */
export type SecaoDocumento =
  | 'associado'
  | 'veiculo'
  | 'vistoria'
  | 'checklist'
  | 'observacao'
  | 'fotos'
  | 'assinatura'
  | 'pdf';

interface PropsDocumentoVistoria {
  associado: { nome?: string | null; cpf?: string | null; email?: string | null };
  veiculo: {
    placa?: string | null;
    modelo?: string | null;
    anoModelo?: number | string | null;
    anoFabricacao?: number | string | null;
  };
  checklist: ChecklistAgrupado;
  observacao?: string | null;
  /** Já normalizadas e **na ordem final** — ver o aviso sobre "burro". */
  fotos?: readonly FotoDoDocumento[];
  assinaturaUrl?: string | null;
  pdfUrl?: string | null;
  /** Só a prévia tem — é o consultor logado. */
  vistoriador?: { nome?: string | null; email?: string | null };
  /** Data/hora exibida na seção "Dados da Vistoria", já formatada. */
  dataVistoria?: string | null;
  /**
   * Liga/desliga seções explicitamente.
   *
   * ⚠️ Serve aos dois casos que "mostrar se houver dado" não resolve: a **prévia**
   * precisa da seção "Dados da Vistoria" (que não existe nas outras) e o **laudo**
   * precisa mostrar "PDF" mesmo **sem** link, porque é onde o consultor vê que o
   * documento ainda não foi gerado.
   */
  secoes?: Partial<Record<SecaoDocumento, boolean>>;
}

/** Par rótulo/valor — o mesmo desenho da tela de captura (at-63). */
const Dado: React.FC<{ rotulo: string; valor?: React.ReactNode }> = ({ rotulo, valor }) => (
  <div className="tw-min-w-0">
    <dt className="tw-text-[10px] tw-font-bold tw-uppercase tw-tracking-wide tw-text-texto-desabilitado">
      {rotulo}
    </dt>
    <dd className="tw-m-0 tw-break-words tw-text-sm tw-font-semibold tw-text-texto-corpo">
      {valor === null || valor === undefined || valor === '' ? '—' : valor}
    </dd>
  </div>
);

/** Texto de "esta seção não tem conteúdo" — nunca uma seção vazia e muda. */
const Vazio: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <p className="tw-m-0 tw-text-sm tw-text-texto-suave">{children}</p>
);

/**
 * Um dos três grupos do checklist.
 *
 * ⚠️ **A cor aqui é do resultado, não de pendência do sistema**: RUIM é `perigo`
 * porque é defeito encontrado no veículo. É o oposto do chip de envio ao SGA, que
 * é âmbar porque "ainda não" não é erro (`utils/vistoriaFluxo.ts`).
 */
const GrupoChecklist: React.FC<{
  titulo: string;
  itens: readonly string[];
  variante: 'ok' | 'perigo' | 'neutro';
}> = ({ titulo, itens, variante }) => (
  <div className="tw-rounded-xl tw-border tw-border-superficie-borda tw-p-3">
    <div className="tw-mb-2 tw-flex tw-items-center tw-justify-between tw-gap-2">
      <span className="tw-text-[11px] tw-font-bold tw-uppercase tw-tracking-wide tw-text-texto-corpo">
        {titulo}
      </span>
      {/* Contagem no cabeçalho: com 23 itens quase sempre em BOM, o número diz o
          essencial antes de o leitor percorrer a lista (at-24). */}
      <Chip variante={variante}>{itens.length}</Chip>
    </div>

    {itens.length === 0 ? (
      <Vazio>Nenhum item</Vazio>
    ) : (
      <ul className="tw-m-0 tw-list-disc tw-pl-5 tw-text-sm tw-text-texto-corpo">
        {itens.map((rotulo) => (
          <li key={rotulo}>{rotulo}</li>
        ))}
      </ul>
    )}
  </div>
);

const DocumentoVistoria: React.FC<PropsDocumentoVistoria> = ({
  associado,
  veiculo,
  checklist,
  observacao,
  fotos,
  assinaturaUrl,
  pdfUrl,
  vistoriador,
  dataVistoria,
  secoes,
}) => {
  /** `secoes` manda; sem ela, mostra quando há dado. */
  const mostrar = (secao: SecaoDocumento, temDado: boolean) => secoes?.[secao] ?? temDado;

  return (
    <div className="tw-flex tw-flex-col tw-gap-6">
      {mostrar('associado', true) && (
        <FormSection titulo="Dados do associado" icone="pessoa">
          <dl className="tw-m-0 tw-grid tw-grid-cols-1 tw-gap-3 sm:tw-grid-cols-2">
            <Dado rotulo="Nome" valor={associado.nome} />
            <Dado rotulo="CPF/CNPJ" valor={associado.cpf} />
            {associado.email !== undefined && (
              <Dado rotulo="E-mail" valor={associado.email} />
            )}
          </dl>
        </FormSection>
      )}

      {mostrar('veiculo', true) && (
        <FormSection titulo="Dados do veículo" icone="veiculo">
          <dl className="tw-m-0 tw-grid tw-grid-cols-2 tw-gap-3 sm:tw-grid-cols-4">
            <Dado rotulo="Placa" valor={veiculo.placa} />
            <Dado rotulo="Modelo" valor={veiculo.modelo} />
            <Dado rotulo="Ano modelo" valor={veiculo.anoModelo} />
            <Dado rotulo="Ano fabricação" valor={veiculo.anoFabricacao} />
          </dl>
        </FormSection>
      )}

      {mostrar('vistoria', Boolean(dataVistoria || vistoriador)) && (
        <FormSection titulo="Dados da vistoria" icone="vistoria">
          <dl className="tw-m-0 tw-grid tw-grid-cols-1 tw-gap-3 sm:tw-grid-cols-2">
            <Dado rotulo="Data/hora" valor={dataVistoria} />
            {vistoriador !== undefined && (
              <Dado rotulo="Vistoriador" valor={vistoriador?.nome || vistoriador?.email} />
            )}
          </dl>
        </FormSection>
      )}

      {mostrar('checklist', true) && (
        <FormSection titulo="Checklist de acessórios" icone="check">
          <div className="tw-grid tw-grid-cols-1 tw-gap-3 md:tw-grid-cols-3">
            <GrupoChecklist titulo="Bom" itens={checklist.bom} variante="ok" />
            <GrupoChecklist titulo="Ruim" itens={checklist.ruim} variante="perigo" />
            <GrupoChecklist
              titulo="Não existente"
              itens={checklist.naoExistente}
              variante="neutro"
            />
          </div>
        </FormSection>
      )}

      {mostrar('observacao', true) && (
        <FormSection titulo="Observação" icone="contato">
          {observacao?.trim() ? (
            /* `whitespace-pre-wrap`: a observação é digitada num textarea e pode
               ter quebras de linha que fazem parte do que o consultor escreveu. */
            <p className="tw-m-0 tw-whitespace-pre-wrap tw-break-words tw-text-sm tw-text-texto-corpo">
              {observacao}
            </p>
          ) : (
            <Vazio>Nenhuma observação registrada.</Vazio>
          )}
        </FormSection>
      )}

      {mostrar('fotos', true) && (
        <FormSection titulo={`Imagens${fotos?.length ? ` (${fotos.length})` : ''}`} icone="vistoria">
          {fotos && fotos.length > 0 ? (
            <div className="tw-grid tw-grid-cols-2 tw-gap-3 sm:tw-grid-cols-3 md:tw-grid-cols-4">
              {fotos.map((foto, indice) => (
                <MiniaturaFotoVistoria
                  key={`${foto.url ?? 'sem-url'}-${indice}`}
                  src={foto.url}
                  legenda={foto.legenda ?? ''}
                  avaria={foto.avaria}
                  acoes={foto.acoes}
                />
              ))}
            </div>
          ) : (
            <Vazio>Nenhuma imagem para exibir.</Vazio>
          )}
        </FormSection>
      )}

      {mostrar('assinatura', true) && (
        <FormSection titulo="Assinatura" icone="pessoa">
          {assinaturaUrl ? (
            <img
              src={assinaturaUrl}
              alt="Assinatura do associado"
              className="tw-max-h-40 tw-w-full tw-max-w-md tw-rounded-xl tw-border tw-border-superficie-borda tw-bg-white tw-object-contain"
            />
          ) : (
            <Vazio>Sem assinatura para exibir.</Vazio>
          )}
        </FormSection>
      )}

      {mostrar('pdf', Boolean(pdfUrl)) && (
        <FormSection titulo="PDF do laudo" icone="adesoes">
          {pdfUrl ? (
            <a
              href={pdfUrl}
              target="_blank"
              rel="noreferrer"
              className="tw-text-sm tw-font-bold tw-text-brand tw-underline"
            >
              Abrir PDF do laudo
            </a>
          ) : (
            <Vazio>O PDF é gerado ao finalizar a vistoria.</Vazio>
          )}
        </FormSection>
      )}
    </div>
  );
};

export default DocumentoVistoria;
